import { useState } from 'react';
import { AuthApiError } from '@supabase/supabase-js';
import {
    supabase,
    probeSupabaseAnonKey,
    getSupabaseConfigError,
    clearLocalAuthSession,
    mapSupabaseAuthError,
} from '../../../lib/supabase';
import {
    buildInvitePath,
    getPendingInviteToken,
    persistInviteToken,
} from '../../../services/modules/organizationService';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isInvalidCredentialsError(error: unknown): boolean {
    return (
        error instanceof AuthApiError &&
        (
            error.code === 'invalid_credentials' ||
            (error.status === 400 && /invalid login/i.test(error.message))
        )
    );
}

export const useLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const inviteToken = getPendingInviteToken();

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoggingIn(true);
        setError(null);

        const normalizedEmail = email.trim().toLowerCase();

        const configError = getSupabaseConfigError();
        if (configError) {
            setError(configError);
            setIsLoggingIn(false);
            return;
        }

        if (!normalizedEmail || !password) {
            setError('Informe e-mail e senha.');
            setIsLoggingIn(false);
            return;
        }

        if (!EMAIL_PATTERN.test(normalizedEmail)) {
            setError('Informe um e-mail válido.');
            setIsLoggingIn(false);
            return;
        }

        const probeError = await probeSupabaseAnonKey();
        if (probeError?.includes('ausente') || probeError?.includes('inválida')) {
            setError(probeError);
            setIsLoggingIn(false);
            return;
        }

        try {
            const { data: { session: existingSession } } = await supabase.auth.getSession();
            const existingEmail = existingSession?.user?.email?.trim().toLowerCase();

            if (existingSession && existingEmail && existingEmail !== normalizedEmail) {
                await clearLocalAuthSession();
            }

            const { data, error: signInError } = await supabase.auth.signInWithPassword({
                email: normalizedEmail,
                password,
            });

            if (signInError) {
                throw signInError;
            }

            if (!data.session) {
                setError('Confirme seu e-mail antes de entrar. Verifique sua caixa de entrada.');
                return;
            }

            if (inviteToken) {
                persistInviteToken(inviteToken);
                window.history.replaceState(null, '', buildInvitePath(inviteToken));
                return;
            }
        } catch (err: unknown) {
            if (!isInvalidCredentialsError(err)) {
                console.warn('[useLogin] Auth rejected:', err);
            }
            setError(mapSupabaseAuthError(err));
        } finally {
            setIsLoggingIn(false);
        }
    };

    return {
        email,
        setEmail,
        password,
        setPassword,
        isLoggingIn,
        error,
        inviteToken,
        handleLogin
    };
};
